import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface HomeImage {
  url: string;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface HomeStat {
  id: number;
  label: string;
  value: number;
  suffix: string;
}

export interface HomeHowItWorksStep {
  id: number;
  icon: string;
  title: string;
  description: string;
}

export interface HomeTestimonial {
  id: number;
  name: string;
  role: string;
  company?: string | null;
  text: string;
  avatar: string;
}

export interface HomeFaqItem {
  id: number;
  question: string;
  answer: string;
}

export interface HomePageData {
  id: number;
  
  // Hero section
  heroTitle: string;
  
  // Value proposition section
  valuePropTitle?: string | null;
  valuePropSubtitle?: string | null;
  valuePropText?: string | null;
  
  // Stats section
  stats: HomeStat[];
  
  // How it works section
  howItWorksTitle?: string | null;
  howItWorksSubtitle?: string | null;
  howItWorksSteps: HomeHowItWorksStep[];
  
  // For Professionals section
  professionalsTitle?: string | null;
  professionalsSubtitle?: string | null;
  professionalsText?: string | null;
  professionalsCtaText?: string | null;
  professionalsImageUrl?: string | null;
  
  // For Employers section
  employersTitle?: string | null;
  employersSubtitle?: string | null;
  employersText?: string | null;
  employersCtaText?: string | null;
  employersImageUrl?: string | null;
  
  // Featured jobs
  featuredJobsTitle?: string | null;
  featuredJobsCtaText?: string | null;
  featuredJobsLimit: number;
  
  // Blog section
  blogSectionTitle?: string | null;
  blogSectionCtaText?: string | null;
  
  // Testimonials
  testimonialsTitle?: string | null;
  testimonials: HomeTestimonial[];
  
  // FAQ
  faqTitle?: string | null;
  faqItems: HomeFaqItem[];
  
  // CTA
  ctaTitle?: string | null;
  ctaSubtitle?: string | null;
  ctaPrimaryButtonText?: string | null;
  ctaSecondaryButtonText?: string | null;

  // SEO
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;

  publishedAt?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class HomePageService {
  private readonly apiUrl = `${environment.contentApiUrl}/api/home-page`;

  constructor(private http: HttpClient) {}

  getHomePage(): Observable<HomePageData | null> {
    return this.http.get<HomePageData>(this.apiUrl).pipe(
      map((data) => data || null),
      catchError((error) => {
        console.error('Error fetching home page:', error);
        return of(null);
      })
    );
  }
}
